/**
 * The rule pack contract (FR-CL-04).
 *
 * This is the verbatim shape of C:\WFA\ticket-ana\agent_rules.json, which is
 * the measured artefact from 4,812 real tickets. It is loaded as versioned
 * DATA — never compiled in — so a tenant admin can ship a new pack without a
 * deployment, and so a candidate pack can be replayed against history before
 * it goes live (FR-CL-07).
 */
import { z } from 'zod';

export const patternRuleSchema = z.object({
  label: z.string().min(1),
  /** Case-insensitive regex, applied to `subject || ' || ' || strippedBody`. */
  pattern: z.string().min(1),
});
export type PatternRule = z.infer<typeof patternRuleSchema>;

export const templateRuleSchema = patternRuleSchema.extend({
  /**
   * The request type this template maps to with no judgement required.
   *
   * May be EMPTY: the real pack's "Portal/self-service form" template
   * identifies the shape of a submission without implying what is being asked
   * for. Such a template is recorded as provenance but does NOT settle the
   * request type — the ladder falls through to rung 4.
   */
  intent: z.string(),
});
export type TemplateRule = z.infer<typeof templateRuleSchema>;

export const routingEntrySchema = z.object({
  queue: z.string().min(1),
  /**
   * Share of this request type's tickets that landed in `queue` over the
   * measured window. Carried through as the router's confidence (FR-LC-06) —
   * it is an observed outcome, not a guess, which is what makes it safe to
   * threshold on.
   */
  purity_pct: z.number().min(0).max(100),
  tickets: z.number().int().nonnegative(),
  alternates: z.record(z.string(), z.number().int().nonnegative()).default({}),
});
export type RoutingEntry = z.infer<typeof routingEntrySchema>;

export const rulePackSchema = z.object({
  meta: z
    .object({
      source_export: z.string().optional(),
      tickets: z.number().optional(),
      window: z.string().optional(),
      note: z.string().optional(),
    })
    .passthrough()
    .optional(),
  /** Rung 2. Six patterns cover every non-actionable ticket in the record. */
  noise_filters: z.array(patternRuleSchema),
  /** Rung 3. Five body templates, fixed request type, field extraction only. */
  templates: z.array(templateRuleSchema),
  /** Rung 4 candidates. */
  intents: z.array(patternRuleSchema),
  /**
   * Rung 4 tie-break order — MOST SPECIFIC FIRST. Resolve by priority, not by
   * score (FR-CL-03). Break/fix and How-to sit last because their patterns
   * fire on almost anything, which is exactly why they may only win when
   * nothing else did.
   */
  intent_priority: z.array(z.string()),
  /** Rung 5. Multi-label: a ticket carries a SET of systems, never one. */
  systems: z.array(patternRuleSchema),
  /** Rung 7. */
  routing: z.record(z.string(), routingEntrySchema),
});
export type RulePack = z.infer<typeof rulePackSchema>;

/** The residual bucket. Not a category — a leftover (Appendix A). */
export const UNCLASSIFIED = 'Unclassified';

/**
 * Request types the six-month record shows are genuinely ambiguous. Below the
 * confidence threshold these are flagged for a human rather than auto-routed
 * (FR-LC-06); the tie-breaker in each case is a requester attribute, not the
 * ticket text.
 */
export const AMBIGUOUS_INTENT_TIEBREAKS: Record<string, string> = {
  'Travel / vehicle / facilities booking': 'requester_office',
  'Facilities / office / event support': 'requester_office',
  'Procurement / purchase request': 'system_named',
};
